import { PageStatus } from '@/types/store';
import { DRAFT_FUNNEL_PAGE_ID } from '../../../constants/funnels.constants';
import {
    type FunnelPageRelationship,
    type FunnelPageSlot,
    getFunnelPageBranchTag,
    getFunnelPageRelationshipLabel,
    sortFunnelPageBranches,
} from '../../../logic';
import type { FunnelPageProductInfo } from '../../../types';
import { FunnelPageNavRow } from './FunnelPageNavRow';

type PageSlot = Extract<FunnelPageSlot, { kind: 'page' }>;

interface FunnelPageNavGroupProps {
    slot: PageSlot;
    parentStepNumber: number | null;
    selectedPageId: number | null;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    relationshipByPageId: Map<number, FunnelPageRelationship>;
    positionByPageId: Map<number, number>;
    onSelectPage: (pageId: number) => void;
    newlyAddedPageId?: number | null;
    isAncestorHidden?: boolean;
}

export const FunnelPageNavGroup = ({
    slot,
    parentStepNumber,
    selectedPageId,
    productInfoByPageId,
    relationshipByPageId,
    positionByPageId,
    onSelectPage,
    newlyAddedPageId = null,
    isAncestorHidden = false,
}: FunnelPageNavGroupProps) => {
    const { page } = slot;
    const relationship = relationshipByPageId.get(page.page_id);
    const isHidden = page.status === PageStatus.DRAFT && page.page_id !== DRAFT_FUNNEL_PAGE_ID;
    const childrenAncestorHidden = isAncestorHidden || isHidden;
    const isBranch = relationship?.kind === 'upsell' || relationship?.kind === 'downsell';
    const stepNumber = positionByPageId.get(page.page_id) ?? slot.stepNumber;

    const branchChildren = (slot.children ?? []).filter((c): c is PageSlot => c.kind === 'page');
    const sortedBranches = sortFunnelPageBranches(branchChildren, relationshipByPageId);

    return (
        <div className='flex flex-col gap-1.5'>
            <FunnelPageNavRow
                stepNumber={stepNumber}
                productInfo={productInfoByPageId[page.page_id]}
                fallbackTitle={page.title}
                subtitle={isBranch ? undefined : getFunnelPageRelationshipLabel(relationship)}
                branchTag={isBranch ? getFunnelPageBranchTag(relationship) : undefined}
                parentStepNumber={parentStepNumber ?? undefined}
                isHidden={isHidden}
                isAncestorHidden={isAncestorHidden}
                isNew={page.page_id === newlyAddedPageId}
                active={page.page_id === selectedPageId}
                onClick={() => onSelectPage(page.page_id)}
            />

            {sortedBranches.length > 0 && (
                <div className='border-border-subtle ml-3 flex flex-col gap-1.5 border-l pl-3'>
                    {sortedBranches.map(branch => (
                        <FunnelPageNavGroup
                            key={branch.page.page_id}
                            slot={branch}
                            parentStepNumber={stepNumber}
                            selectedPageId={selectedPageId}
                            productInfoByPageId={productInfoByPageId}
                            relationshipByPageId={relationshipByPageId}
                            positionByPageId={positionByPageId}
                            onSelectPage={onSelectPage}
                            newlyAddedPageId={newlyAddedPageId}
                            isAncestorHidden={childrenAncestorHidden}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
